/**
 * Aus Sealed product-page and cart behaviours.
 * - <as-dispatch>: countdown to the same-day dispatch cutoff + arrival window
 * - <as-tiers>: quantity tier buttons that drive the native quantity selector
 * - <as-addons>: add-on checkboxes that ride along with the main Add to cart
 * - [data-as-add]: one-click add buttons (cart drawer upsells)
 * All cart changes are announced with the standard CartLinesUpdateEvent so the
 * Horizon cart drawer and cart icon refresh themselves.
 */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const money = (cents, format) => {
  const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (format || '${{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
};

function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

/**
 * Add one or more variants to the cart and announce it to the theme.
 * @param {Array<{id: number|string, quantity: number}>} items
 * @param {'product'|'cart'} context
 */
export async function addItems(items, context = 'product') {
  if (!items.length) return null;
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context,
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const res = await fetch(addUrl.endsWith('.js') ? addUrl : addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const cartRes = await fetch(cartUrl.endsWith('.js') ? cartUrl : cartUrl + '.js', {
      headers: { Accept: 'application/json' },
    });
    const cart = await cartRes.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: data.sections, source: 'as-product', didError: false },
    });
    return cart;
  } catch (err) {
    console.error('[as-product]', err);
    deferred.reject(err);
    return null;
  }
}

/* ---------- Dispatch countdown ---------- */
class AsDispatch extends HTMLElement {
  connectedCallback() {
    this.tz = this.dataset.tz || 'Australia/Brisbane';
    this.cutoff = Number(this.dataset.cutoff || 14);
    this.expMin = Number(this.dataset.expressMin || 1);
    this.expMax = Number(this.dataset.expressMax || 3);
    this.stdMin = Number(this.dataset.standardMin || 2);
    this.stdMax = Number(this.dataset.standardMax || 6);
    this.render();
    this.timer = setInterval(() => this.render(), 30000);
  }
  disconnectedCallback() {
    clearInterval(this.timer);
  }
  /** Current wall-clock in the store timezone as {y,m,d,h,min,dow} */
  now() {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: this.tz,
      hour12: false,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      weekday: 'short',
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    return { y: +get('year'), m: +get('month'), d: +get('day'), h: +get('hour') % 24, min: +get('minute'), dow };
  }
  addDays(dateUTC, n) {
    const d = new Date(dateUTC);
    d.setUTCDate(d.getUTCDate() + n);
    return d;
  }
  addBusinessDays(dateUTC, n) {
    let d = new Date(dateUTC);
    let left = n;
    while (left > 0) {
      d = this.addDays(d, 1);
      if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) left -= 1;
    }
    return d;
  }
  fmt(dateUTC) {
    return new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).format(dateUTC);
  }
  render() {
    const n = this.now();
    const today = new Date(Date.UTC(n.y, n.m - 1, n.d));
    const isBiz = n.dow >= 1 && n.dow <= 5;
    const beforeCutoff = n.h < this.cutoff;
    let dispatch; // UTC-midnight date representing the dispatch day
    let lead;
    if (isBiz && beforeCutoff) {
      const mins = (this.cutoff - n.h) * 60 - n.min;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      lead = `Order within <strong>${h > 0 ? h + 'h ' : ''}${m}m</strong> for same-day dispatch`;
      dispatch = today;
    } else {
      dispatch = this.addBusinessDays(today, 1);
      lead = `Order now, dispatches <strong>${this.fmt(dispatch)}</strong>`;
    }
    const exp = `${this.fmt(this.addBusinessDays(dispatch, this.expMin))}–${this.fmt(this.addBusinessDays(dispatch, this.expMax))}`;
    const std = `${this.fmt(this.addBusinessDays(dispatch, this.stdMin))}–${this.fmt(this.addBusinessDays(dispatch, this.stdMax))}`;
    const leadEl = this.querySelector('[data-lead]');
    const expEl = this.querySelector('[data-express]');
    const stdEl = this.querySelector('[data-standard]');
    if (leadEl) leadEl.innerHTML = lead;
    if (expEl) expEl.textContent = exp;
    if (stdEl) stdEl.textContent = std;
  }
}
if (!customElements.get('as-dispatch')) customElements.define('as-dispatch', AsDispatch);

/* ---------- Quantity tiers ---------- */
class AsTiers extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const btn = e.target instanceof Element ? e.target.closest('button[data-qty]') : null;
      if (!btn || btn.disabled) return;
      const qty = Number(btn.dataset.qty);
      const section = this.closest('.shopify-section') || document;
      const qs = section.querySelector('product-form-component quantity-selector-component');
      if (qs) {
        if (typeof qs.setValue === 'function') qs.setValue(String(qty));
        else {
          const input = qs.querySelector('input[name="quantity"]');
          if (input) input.value = String(qty);
        }
        if (typeof qs.onQuantityChange === 'function') qs.onQuantityChange();
        if (typeof qs.updateButtonStates === 'function') qs.updateButtonStates();
      }
      this.querySelectorAll('button[data-qty]').forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
    });
    // keep the highlight in sync if the shopper edits the quantity box directly
    const section = this.closest('.shopify-section') || document;
    section.addEventListener('change', (e) => {
      if (e.target instanceof HTMLInputElement && e.target.name === 'quantity') {
        const q = Number(e.target.value);
        this.querySelectorAll('button[data-qty]').forEach((b) => b.setAttribute('aria-pressed', Number(b.dataset.qty) === q ? 'true' : 'false'));
      }
    });
  }
}
if (!customElements.get('as-tiers')) customElements.define('as-tiers', AsTiers);

/* ---------- Add-ons that ride along with the main Add to cart ---------- */
class AsAddons extends HTMLElement {
  connectedCallback() {
    this.format = this.dataset.moneyFormat || '${{amount}}';
    this.addEventListener('change', () => this.updateTotal());
    this.updateTotal();
    this.onCartUpdate = (event) => {
      const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
      const section = this.closest('.shopify-section');
      if (!form || !section || !section.contains(form)) return;
      if (event.action !== 'add' || event.detail?.source === 'as-product') return;
      const items = this.checkedItems();
      if (!items.length) return;
      event.promise?.then(async (result) => {
        if (result?.detail?.didError) return;
        const cart = await addItems(items, 'product');
        if (cart) {
          this.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => (cb.checked = false));
          this.updateTotal();
        }
      }).catch(() => {});
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }
  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }
  checkedItems() {
    return [...this.querySelectorAll('input[type="checkbox"]:checked')].map((cb) => ({ id: Number(cb.value), quantity: 1 }));
  }
  updateTotal() {
    const extra = [...this.querySelectorAll('input[type="checkbox"]:checked')].reduce((s, cb) => s + Number(cb.dataset.price || 0), 0);
    const el = this.querySelector('[data-addons-total]');
    const wrap = this.querySelector('[data-addons-summary]');
    if (el) el.textContent = money(extra, this.format);
    if (wrap) wrap.hidden = extra === 0;
  }
}
if (!customElements.get('as-addons')) customElements.define('as-addons', AsAddons);

/* ---------- One-click add buttons (cart drawer upsells etc.) ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target instanceof Element ? e.target.closest('button[data-as-add]') : null;
  if (!btn) return;
  e.preventDefault();
  if (btn.disabled) return;
  btn.disabled = true;
  const original = btn.textContent;
  btn.textContent = btn.dataset.asAdding || 'Adding…';
  const cart = await addItems([{ id: Number(btn.dataset.asAdd), quantity: 1 }], btn.closest('cart-drawer-component') ? 'cart' : 'product');
  if (!cart) {
    btn.disabled = false;
    btn.textContent = original;
  }
});
